import React from "react";
import {
  RouterProvider,
  createBrowserRouter,
  Navigate,
} from "react-router-dom";
import { CompanyProvider } from "./contexts/CompanyContext";
import { Layout } from "./components/Layout";
import { Dashboard } from "./pages/Dashboard";
import { Bookings } from "./pages/Bookings";
import { BookingWizardPage } from "./pages/BookingWizardPage";
import { CompanyInfo } from "./pages/CompanyInfo";
import { Auth } from "./pages/Auth";
import { Account } from "./pages/Account";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Analytics } from "@vercel/analytics/react";
import { BookingTrackerPage } from "./pages/BookingTrackerPage";
import { BookingEditPage } from "./pages/BookingEditPage";
import { BookingViewPage } from "./pages/BookingViewPage";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { AuthGuard } from "./components/AuthGuard";
import { MyProducts } from "./pages/MyProducts";
import { Invoices } from "./pages/Invoices";
import { LanguageProvider } from "./contexts/LanguageContext";
import { EmailVerified } from "./pages/EmailVerified";
import { RegenerateLabelPage } from "./pages/RegenerateLabelPage";
import { RegeneratePackingSlipPage } from "./pages/RegeneratePackingSlipPage";
import { PostHogProvider } from "posthog-js/react";
import { PostHogErrorBoundary } from "./components/PostHogErrorBoundary";
import { getPostHogConfig } from "./lib/env";
import { ResetPassword } from "./pages/ResetPassword";
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
import { FeatureFlagProvider } from "./contexts/FeatureFlagContext";
import { AdminGuard } from "./guards/AdminGuard";
import { FAQ } from "./pages/FAQ";
import AdminDashboard from "./pages/AdminDashboard";
import { ErrorTrackingInitializer } from "./components/ErrorTrackingInitializer";
import TasksAdmin from "./pages/TasksAdmin";
import UsersAdmin from "./pages/UsersAdmin";
import PortsAdmin from "./pages/PortsAdmin";
import TariffsAdmin from "./pages/TariffsAdmin";
import CompaniesAdmin from "./pages/CompaniesAdmin";
import BookingsAdmin from "./pages/BookingsAdmin";
import ContainersAdmin from "./pages/ContainersAdmin";
import ContainerUnloadingPage from "./pages/ContainerUnloadingPage";
import ContainerDetailsPage from "./pages/ContainerDetailsPage";
import ReportsAdmin from "./pages/ReportsAdmin";
import Tools from "./pages/Tools";
import LVBPalletCalculator from "./pages/LVBPalletCalculator";
import HSCodeValidator from "./pages/HSCodeValidator";
import InvoicingAdmin from "./pages/InvoicingAdmin";
import InvoicesAdmin from "./pages/InvoicesAdmin";

// PostHog configuration - legacy approach, now using getPostHogConfig()
// Keeping for reference
/*
const posthogApiKey =
  import.meta.env.VITE_POSTHOG_API_KEY ||
  import.meta.env.VITE_PUBLIC_POSTHOG_KEY ||
  import.meta.env.POSTHOG_API_KEY ||
  import.meta.env.REACT_APP_POSTHOG_API_KEY ||
  "";

const posthogHost =
  import.meta.env.VITE_POSTHOG_HOST ||
  import.meta.env.VITE_PUBLIC_POSTHOG_HOST ||
  import.meta.env.POSTHOG_HOST ||
  import.meta.env.REACT_APP_POSTHOG_HOST ||
  "https://app.posthog.com";

// Log PostHog config for debugging production issues
console.log("App.tsx PostHog config:", {
  hasApiKey: !!posthogApiKey,
  apiHost: posthogHost,
  environment: import.meta.env.MODE || "unknown",
});
*/

// Initialize PostHog
// initPostHog();

// Add logging to track route access
console.log("🚀 App: Creating router with routes");

// Create a wrapper component for logging
const ResetPasswordWithLogging = () => {
  console.log("🔄 Router: /reset-password route accessed");
  return <ResetPassword />;
};

const router = createBrowserRouter([
  {
    path: "/auth",
    element: (
      <AuthGuard noAuth>
        <Auth />
      </AuthGuard>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/reset-password",
    element: <ResetPasswordWithLogging />,
  },
  {
    path: "/privacy-policy",
    element: <PrivacyPolicy />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/email-verified",
    element: <EmailVerified />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/regenerate-label",
    element: <RegenerateLabelPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/regenerate-packingslip",
    element: <RegeneratePackingSlipPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/faq",
    element: <FAQ />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/booking/:trackingNumber",
    element: <BookingTrackerPage />,
  },
  {
    path: "/admin/containers/:containerId/unload",
    element: (
      <AuthGuard>
        <ContainerUnloadingPage />
      </AuthGuard>
    ),
  },
  {
    element: <Layout />,
    errorElement: <ErrorBoundary />,
    children: [
      { path: "/", element: <Navigate to="/dashboard" replace /> },
      {
        path: "dashboard",
        element: (
          <AuthGuard>
            <Dashboard />
          </AuthGuard>
        ),
      },
      {
        path: "company-info",
        element: (
          <AuthGuard>
            <CompanyInfo />
          </AuthGuard>
        ),
      },
      {
        path: "account",
        element: (
          <AuthGuard>
            <Account />
          </AuthGuard>
        ),
      },
      {
        path: "my-products",
        element: (
          <AuthGuard>
            <MyProducts />
          </AuthGuard>
        ),
      },
      {
        path: "invoices",
        element: (
          <AuthGuard>
            <Invoices />
          </AuthGuard>
        ),
      },
      {
        path: "tools",
        children: [
          {
            index: true,
            element: (
              <AuthGuard>
                <Tools />
              </AuthGuard>
            ),
          },
          {
            path: "lvb-pallet-calculator",
            element: (
              <AuthGuard>
                <LVBPalletCalculator />
              </AuthGuard>
            ),
          },
          {
            path: "hs-code-validator",
            element: (
              <AuthGuard>
                <HSCodeValidator />
              </AuthGuard>
            ),
          },
        ],
      },
      {
        path: "admin",
        element: (
          <AuthGuard>
            <AdminGuard />
          </AuthGuard>
        ),
        children: [
          {
            index: true,
            element: <AdminDashboard />,
          },
          {
            path: "invoices",
            element: <InvoicesAdmin />,
          },
          {
            path: "pricing",
            element: <InvoicingAdmin />,
          },
          {
            path: "tasks",
            element: <TasksAdmin />,
          },
          {
            path: "users",
            element: <UsersAdmin />,
          },
          {
            path: "ports",
            element: <PortsAdmin />,
          },
          {
            path: "tariffs",
            element: <TariffsAdmin />,
          },
          {
            path: "companies",
            element: <CompaniesAdmin />,
          },
          {
            path: "bookings",
            element: <BookingsAdmin />,
          },
          {
            path: "containers",
            element: <ContainersAdmin />,
          },
          {
            path: "containers/:containerId",
            element: <ContainerDetailsPage />,
          },
          {
            path: "reports",
            element: <ReportsAdmin />,
          },
        ],
      },
      {
        path: "bookings",
        children: [
          {
            index: true,
            element: (
              <AuthGuard>
                <Bookings />
              </AuthGuard>
            ),
          },
          {
            path: "new",
            element: (
              <AuthGuard>
                <BookingWizardPage />
              </AuthGuard>
            ),
          },
          {
            path: ":id/edit",
            element: (
              <AuthGuard>
                <BookingEditPage />
              </AuthGuard>
            ),
          },
          {
            path: ":id/view",
            element: (
              <AuthGuard>
                <BookingViewPage />
              </AuthGuard>
            ),
          },
          {
            path: "track/:trackingNumber",
            element: <BookingTrackerPage />,
          },
        ],
      },
    ],
  },
]);

console.log("🚀 App: Router created successfully");

function App() {
  // Use the helper function to get PostHog config
  const posthogConfig = getPostHogConfig();

  console.log("[APP RENDER] PostHog configuration:", {
    ...posthogConfig.debug,
    apiHost: posthogConfig.apiHost,
    // Add info about direct environment variable access
    directEnvCheck: {
      VITE_PUBLIC_POSTHOG_KEY: import.meta.env.VITE_PUBLIC_POSTHOG_KEY
        ? "exists"
        : "missing",
      VITE_PUBLIC_POSTHOG_HOST: import.meta.env.VITE_PUBLIC_POSTHOG_HOST
        ? "exists"
        : "missing",
      VITE_POSTHOG_API_KEY: import.meta.env.VITE_POSTHOG_API_KEY
        ? "exists"
        : "missing",
      VITE_POSTHOG_HOST: import.meta.env.VITE_POSTHOG_HOST
        ? "exists"
        : "missing",
      // Add host value to verify it's correct
      hostValue: posthogConfig.apiHost,
    },
  });

  return (
    <>
      {posthogConfig.hasKey ? (
        <PostHogProvider
          apiKey={posthogConfig.apiKey}
          options={{
            api_host: posthogConfig.apiHost,
            debug: true,
            capture_pageview: false,
            autocapture: true,
            persistence: "localStorage" as const,
            property_blacklist: [
              "$current_url",
              "$initial_referring_domain",
              "password",
            ],
            session_recording: {
              maskAllInputs: true,
            },
          }}
        >
          <PostHogErrorBoundary>
            <LanguageProvider>
              <CompanyProvider>
                <FeatureFlagProvider>
                  <ErrorTrackingInitializer />
                  <RouterProvider router={router} />
                </FeatureFlagProvider>
                <SpeedInsights />
                <Analytics />
              </CompanyProvider>
            </LanguageProvider>
          </PostHogErrorBoundary>
        </PostHogProvider>
      ) : (
        // Render app without PostHog if no API key is found
        <LanguageProvider>
          <CompanyProvider>
            <FeatureFlagProvider>
              <RouterProvider router={router} />
            </FeatureFlagProvider>
            <SpeedInsights />
            <Analytics />
          </CompanyProvider>
        </LanguageProvider>
      )}
    </>
  );
}

export default App;
